import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { authAPI, promptsAPI, agentsAPI, mcpServersAPI, gamificationAPI, followsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FaStar, FaEye, FaAward, FaCalendar, FaGhost, FaUsers } from 'react-icons/fa';
import FollowButton from '../components/FollowButton';
import SEOHead from '../components/SEOHead';
import { ContentTypeSwitcher, TypeBadge } from '../components/ui';

const BADGE_METADATA = {
  'first-boop': {
    name: 'First Boop',
    description: 'Published your first prompt on Boop.',
  },
  'boop-baron': {
    name: 'Boop Baron',
    description: 'Collected 100 total boops from the community.',
  },
  'efficient-booper': {
    name: 'Efficient Booper',
    description: 'Shared five prompts under 100 tokens.',
  },
  'rating-champion': {
    name: 'Rating Champion',
    description: 'Submitted 50 thoughtful ratings.',
  },
  'fork-master': {
    name: 'Fork Master',
    description: 'Explored and forked 10 community prompts.',
  },
  'prompt-professional': {
    name: 'Prompt Professional',
    description: 'Reached the top Boop level.',
  },
  'consistent-creator': {
    name: 'Consistent Creator',
    description: 'Posted prompts four weeks in a row.',
  },
  'weekly-wonder': {
    name: 'Weekly Wonder',
    description: 'Owned the top-rated prompt of the week.',
  },
};

const normalizeBadge = (badge) => {
  if (!badge) return null;
  const slug = badge.slug || badge.badge_code || '';
  const meta = BADGE_METADATA[slug] || {};
  const displayName =
    badge.name ||
    badge.badge_name ||
    meta.name ||
    slug.replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());

  return {
    badge_code: slug || badge.badge_code || displayName,
    badge_name: displayName,
    description: badge.description || meta.description || 'Unlocked badge.',
    tier: badge.tier,
    icon: badge.icon,
    earned_at: badge.awarded_at || badge.earned_at,
  };
};

const Profile = () => {
  const { username } = useParams();
  const { userProfile: currentUserProfile } = useAuth();
  const [user, setUser] = useState(null);
  const [userPrompts, setUserPrompts] = useState([]);
  const [userAgents, setUserAgents] = useState([]);
  const [userMCPServers, setUserMCPServers] = useState([]);
  const [gamification, setGamification] = useState(null);
  const [followCounts, setFollowCounts] = useState({ followersCount: 0, followingCount: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('content'); // 'content' or 'badges'
  const [contentType, setContentType] = useState('prompt'); // 'prompt', 'agent', or 'mcp_server'
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ display_name: '', bio: '' });

  const isOwnProfile = currentUserProfile && currentUserProfile.username === username;

  const fetchProfileData = useCallback(async () => {
    setLoading(true);
    try {
      const userResponse = await authAPI.getUserByUsername(username);
      const profileUser = userResponse.data.user;
      setUser(profileUser);

      if (isOwnProfile) {
        setProfileForm({
          display_name: profileUser.display_name || '',
          bio: profileUser.bio || '',
        });
      }

      const [promptsResult, agentsResult, serversResult, countsResult, gamificationResult] = await Promise.allSettled([
        promptsAPI.getUserPrompts(profileUser.id, { limit: 20 }),
        agentsAPI.getUserAgents(profileUser.id, { limit: 20 }),
        mcpServersAPI.getUserServers(profileUser.id, { limit: 20 }),
        followsAPI.getCounts(profileUser.id),
        gamificationAPI.getSummary(profileUser.id),
      ]);

      if (promptsResult.status === 'fulfilled') {
        setUserPrompts(promptsResult.value.data.prompts || []);
      }

      if (agentsResult.status === 'fulfilled') {
        setUserAgents(agentsResult.value.data.agents || []);
      }

      if (serversResult.status === 'fulfilled') {
        setUserMCPServers(serversResult.value.data.servers || []);
      }

      if (countsResult.status === 'fulfilled') {
        setFollowCounts(countsResult.value.data);
      } else if (countsResult.status === 'rejected') {
        console.log('Follow counts not available');
      }

      if (gamificationResult.status === 'fulfilled') {
        const summary =
          gamificationResult.value.data?.summary ?? gamificationResult.value.data ?? null;
        const normalizedSummary = summary
          ? {
              ...summary,
              badges: (summary.badges || [])
                .map(normalizeBadge)
                .filter(Boolean),
              recentActivities: summary.recentActivities || [],
            }
          : null;
        setGamification(normalizedSummary);
      } else if (gamificationResult.status === 'rejected') {
        console.log('Gamification data not available');
      }
    } catch (error) {
      console.error('Fetch profile error:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [isOwnProfile, username]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  const handleSaveProfile = async () => {
    try {
      await authAPI.updateProfile({
        display_name: profileForm.display_name,
        bio: profileForm.bio,
      });
      toast.success('Profile updated');
      setEditingProfile(false);
      // Update local user to reflect changes without full refetch
      setUser((prev) => ({ ...prev, display_name: profileForm.display_name, bio: profileForm.bio }));
    } catch (err) {
      console.error('Inline profile update failed', err);
      toast.error('Failed to update profile');
    }
  };

  const getLevelName = (level) => {
    const levels = {
      B: 'Beginner',
      I: 'Intermediate',
      A: 'Advanced',
      E: 'Expert',
      P: 'Professional',
    };
    return levels[level] || 'Beginner';
  };

  const getLevelColor = (level) => {
    const colors = {
      B: 'text-gray-600',
      I: 'text-blue-600',
      A: 'text-purple-600',
      E: 'text-orange-600',
      P: 'text-yellow-600',
    };
    return colors[level] || 'text-gray-600';
  };

  const getBadgeIcon = (badgeCode) => {
    // Map badge codes to icons (simplified version)
    const icons = {
      'first-boop': '🎉',
      'boop-baron': '👑',
      'efficient-booper': '⚡',
      'rating-champion': '🌟',
      'fork-master': '🔱',
      'prompt-professional': '💎',
      'consistent-creator': '🔥',
      'weekly-wonder': '🏆',
    };
    return icons[badgeCode] || '🏅';
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center py-12">Loading profile...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <FaGhost className="text-6xl text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-600">User not found</h2>
          <p className="text-gray-500 mt-2">This user doesn't exist or has been deleted</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <SEOHead
        title={`${user.display_name || user.username}'s Profile`}
        description={user.bio || `Check out ${user.username}'s prompts, badges, and stats on Boop. Level ${user.level || 'B'} prompt engineer.`}
        url={`${window.location.origin}/profile/${user.username}`}
        type="profile"
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Sidebar - User Info */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            {/* Avatar */}
            <div className="text-center mb-4">
              <div className="w-32 h-32 bg-gradient-to-br from-boop-primary to-boop-secondary rounded-full flex items-center justify-center text-5xl font-bold text-white mx-auto mb-4">
                {user.username?.[0]?.toUpperCase()}
              </div>
              <h1 className="text-2xl font-bold mb-1">{user.display_name || user.username}</h1>
              <p className="text-gray-500 mb-3">@{user.username}</p>

              {/* Follow Button */}
              <div className="flex justify-center mb-4">
                <FollowButton
                  userId={user.id}
                  username={user.username}
                  onFollowChange={() => {
                    // Refresh follow counts
                    followsAPI.getCounts(user.id).then(response => {
                      setFollowCounts(response.data);
                    });
                  }}
                />
              </div>

              {/* Follower/Following Counts */}
              <div className="flex gap-4 justify-center text-sm">
                <div className="text-center">
                  <div className="font-bold text-lg text-boop-primary">{followCounts.followersCount}</div>
                  <div className="text-gray-600 flex items-center gap-1">
                    <FaUsers className="text-xs" />
                    Followers
                  </div>
                </div>
                <div className="h-12 w-px bg-gray-300"></div>
                <div className="text-center">
                  <div className="font-bold text-lg text-boop-primary">{followCounts.followingCount}</div>
                  <div className="text-gray-600">Following</div>
                </div>
              </div>
            </div>

            {/* Level Badge */}
            <div className="bg-gradient-to-r from-boop-light to-white p-4 rounded-lg mb-4 text-center">
              <div className="text-sm text-gray-600 mb-1">Level</div>
              <div className={`text-4xl font-bold ${getLevelColor(user.level)}`}>
                {user.level || 'B'}
              </div>
              <div className="text-sm font-semibold text-gray-700">
                {getLevelName(user.level)}
              </div>
            </div>

            {/* Bio */}
            <div className="mb-4 pb-4 border-b">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">Bio</h3>
                {isOwnProfile && (
                  <button
                    onClick={() => setEditingProfile((v) => !v)}
                    className="text-xs text-boop-primary hover:text-boop-secondary font-semibold"
                  >
                    {editingProfile ? 'Cancel' : 'Edit'}
                  </button>
                )}
              </div>
              {!editingProfile && (
                <p className="text-gray-700 text-sm whitespace-pre-line">{user.bio || 'No bio yet.'}</p>
              )}
              {editingProfile && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Display Name</label>
                    <input
                      type="text"
                      value={profileForm.display_name}
                      onChange={(e) => setProfileForm({ ...profileForm, display_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Bio</label>
                    <textarea
                      rows={4}
                      value={profileForm.bio}
                      onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
                      maxLength={500}
                    />
                    <div className="text-xs text-gray-400 mt-1">{profileForm.bio.length}/500</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveProfile}
                      className="px-4 py-2 bg-boop-primary text-white rounded-lg hover:bg-boop-secondary font-semibold"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingProfile(false)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="space-y-3 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm flex items-center gap-1">
                  <span className="text-indigo-500">💬</span> Prompts
                </span>
                <span className="font-bold text-indigo-600">{userPrompts.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm flex items-center gap-1">
                  <span className="text-pink-500">🤖</span> AI Agents
                </span>
                <span className="font-bold text-pink-600">{userAgents.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm flex items-center gap-1">
                  <span className="text-cyan-500">🔌</span> MCP Servers
                </span>
                <span className="font-bold text-cyan-600">{userMCPServers.length}</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600 text-sm">Boops Received</span>
                  <span className="font-bold text-boop-secondary">{user.total_boops_received || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Boops Given</span>
                  <span className="font-bold">{user.total_boops_given || 0}</span>
                </div>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-gray-600 text-sm flex items-center">
                  <FaCalendar className="mr-2" />
                  Joined
                </span>
                <span className="text-sm">{new Date(user.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Account Status */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Status</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    user.account_status === 'active'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {user.account_status || 'active'}
                </span>
              </div>
              {user.is_premium && (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-gray-600">Premium</span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">
                    ⭐ PREMIUM
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Badges */}
          {gamification?.badges && gamification.badges.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FaAward className="mr-2 text-boop-secondary" />
                Badges ({gamification.badges.length})
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {gamification.badges.slice(0, 9).map((badge) => (
                  <div
                    key={badge.id}
                    className="bg-gradient-to-br from-boop-light to-white p-3 rounded-lg text-center hover:shadow-md transition"
                    title={badge.description}
                  >
                    <div className="text-3xl mb-1">{getBadgeIcon(badge.badge_code)}</div>
                    <div className="text-xs font-semibold text-gray-700 truncate">
                      {badge.badge_name}
                    </div>
                  </div>
                ))}
              </div>
              {gamification.badges.length > 9 && (
                <button
                  onClick={() => setActiveTab('badges')}
                  className="w-full mt-3 text-sm text-boop-primary hover:text-boop-secondary font-semibold"
                >
                  View All Badges →
                </button>
              )}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Main Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-1 inline-flex mb-6">
            <button
              onClick={() => setActiveTab('content')}
              className={`px-6 py-2 rounded-lg font-semibold transition ${
                activeTab === 'content'
                  ? 'bg-boop-primary text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:text-boop-primary'
              }`}
            >
              Content
            </button>
            <button
              onClick={() => setActiveTab('badges')}
              className={`px-6 py-2 rounded-lg font-semibold transition ${
                activeTab === 'badges'
                  ? 'bg-boop-primary text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:text-boop-primary'
              }`}
            >
              Badges ({gamification?.badges?.length || 0})
            </button>
          </div>

          {/* Content Type Switcher (shown when on content tab) */}
          {activeTab === 'content' && (
            <div className="flex justify-center mb-6">
              <ContentTypeSwitcher
                activeType={contentType}
                onChange={setContentType}
                types={['prompt', 'agent', 'mcp_server']}
                showCounts={true}
                counts={{
                  prompt: userPrompts.length,
                  agent: userAgents.length,
                  mcp_server: userMCPServers.length
                }}
                variant="tabs"
                size="md"
              />
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div>
              {/* Render content based on selected type */}
              {contentType === 'prompt' && (
                userPrompts.length === 0 ? (
                  <div className="bg-white dark:bg-gray-800 p-12 rounded-lg shadow-md text-center">
                    <FaGhost className="text-6xl text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      {isOwnProfile ? 'You haven\'t created any prompts yet.' : 'No prompts yet.'}
                    </p>
                    {isOwnProfile && (
                      <Link
                        to="/prompts/create"
                        className="inline-block bg-boop-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-boop-secondary transition"
                      >
                        💬 Create Your First Prompt
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {userPrompts.map((prompt) => (
                      <Link
                        key={prompt.id}
                        to={`/prompts/${prompt.id}`}
                        className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-xl transition card-hover border-l-4 border-indigo-500"
                      >
                        <div className="mb-3">
                          <TypeBadge type="prompt" size="sm" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2 line-clamp-2 text-gray-900 dark:text-white">{prompt.title}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">{prompt.description}</p>

                        <div className="flex items-center justify-between text-sm mb-3">
                          <span className="flex items-center text-boop-primary font-semibold">
                            <FaStar className="mr-1" />
                            {(parseFloat(prompt.bayesian_rating) || 0).toFixed(1)}
                          </span>
                          <span className="flex items-center text-gray-500 dark:text-gray-400">
                            <FaEye className="mr-1" />
                            {prompt.views_count || 0}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400">{prompt.total_ratings || 0} ratings</span>
                        </div>

                        <div className="flex gap-2 flex-wrap">
                          {prompt.tags?.slice(0, 3).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>

                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                          {new Date(prompt.created_at).toLocaleDateString()}
                        </div>
                      </Link>
                    ))}
                  </div>
                )
              )}

              {contentType === 'agent' && (
                userAgents.length === 0 ? (
                  <div className="bg-white dark:bg-gray-800 p-12 rounded-lg shadow-md text-center">
                    <FaGhost className="text-6xl text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      {isOwnProfile ? 'You haven\'t created any AI agents yet.' : 'No AI agents yet.'}
                    </p>
                    {isOwnProfile && (
                      <Link
                        to="/agents/create"
                        className="inline-block bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition"
                      >
                        🤖 Create Your First Agent
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {userAgents.map((agent) => (
                      <Link
                        key={agent.id}
                        to={`/agents/${agent.id}`}
                        className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-xl transition card-hover border-l-4 border-pink-500"
                      >
                        <div className="mb-3">
                          <TypeBadge type="agent" size="sm" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2 line-clamp-2 text-gray-900 dark:text-white">{agent.title}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">{agent.description}</p>

                        <div className="flex items-center justify-between text-sm mb-3">
                          <span className="flex items-center text-pink-600 dark:text-pink-400 font-semibold">
                            <FaStar className="mr-1" />
                            {(parseFloat(agent.bayesian_rating || agent.average_rating) || 0).toFixed(1)}
                          </span>
                          <span className="flex items-center text-gray-500 dark:text-gray-400">
                            <FaEye className="mr-1" />
                            {agent.views_count || 0}
                          </span>
                        </div>

                        <div className="flex gap-2 flex-wrap">
                          {agent.tools?.slice(0, 3).map((tool, i) => (
                            <span key={i} className="px-2 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 text-xs rounded-full">
                              {typeof tool === 'string' ? tool : tool.name}
                            </span>
                          ))}
                        </div>

                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                          {new Date(agent.created_at).toLocaleDateString()}
                        </div>
                      </Link>
                    ))}
                  </div>
                )
              )}

              {contentType === 'mcp_server' && (
                userMCPServers.length === 0 ? (
                  <div className="bg-white dark:bg-gray-800 p-12 rounded-lg shadow-md text-center">
                    <FaGhost className="text-6xl text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      {isOwnProfile ? 'You haven\'t created any MCP servers yet.' : 'No MCP servers yet.'}
                    </p>
                    {isOwnProfile && (
                      <Link
                        to="/mcp-servers/create"
                        className="inline-block bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-cyan-700 transition"
                      >
                        🔌 Create Your First MCP Server
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {userMCPServers.map((server) => (
                      <Link
                        key={server.id}
                        to={`/mcp-servers/${server.id}`}
                        className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-xl transition card-hover border-l-4 border-cyan-500"
                      >
                        <div className="mb-3">
                          <TypeBadge type="mcp_server" size="sm" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2 line-clamp-2 text-gray-900 dark:text-white">{server.title}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">{server.description}</p>

                        <div className="flex items-center justify-between text-sm mb-3">
                          <span className="flex items-center text-cyan-600 dark:text-cyan-400 font-semibold">
                            <FaStar className="mr-1" />
                            {(parseFloat(server.bayesian_rating || server.average_rating) || 0).toFixed(1)}
                          </span>
                          <span className="flex items-center text-gray-500 dark:text-gray-400">
                            <FaEye className="mr-1" />
                            {server.views_count || 0}
                          </span>
                        </div>

                        <div className="flex gap-2 flex-wrap">
                          {server.compatible_clients?.slice(0, 3).map((client, i) => (
                            <span key={i} className="px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-xs rounded-full">
                              {client}
                            </span>
                          ))}
                        </div>

                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                          {new Date(server.created_at).toLocaleDateString()}
                        </div>
                      </Link>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {/* Badges Tab */}
          {activeTab === 'badges' && (
            <div>
              {!gamification?.badges || gamification.badges.length === 0 ? (
                <div className="bg-white p-12 rounded-lg shadow-md text-center">
                  <FaAward className="text-6xl text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {isOwnProfile ? 'You haven\'t earned any badges yet.' : 'No badges yet.'}
                  </p>
                  {isOwnProfile && (
                    <p className="text-sm text-gray-400 mt-2">
                      Create prompts, rate others, and participate to earn badges!
                    </p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {gamification.badges.map((badge) => (
                    <div
                      key={badge.id}
                      className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition text-center"
                    >
                      <div className="text-5xl mb-3">{getBadgeIcon(badge.badge_code)}</div>
                      <h4 className="font-semibold text-gray-800 mb-2">{badge.badge_name}</h4>
                      <p className="text-xs text-gray-600 mb-3">{badge.description}</p>
                      <div className="text-xs text-gray-400">
                        Earned: {new Date(badge.earned_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Activity Summary */}
          {gamification?.recentActivities && gamification.recentActivities.length > 0 && (
            <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {gamification.recentActivities.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-center text-sm border-b pb-3 last:border-b-0">
                    <div className="flex-1">
                      <span className="text-gray-700">{activity.activity_type}</span>
                      {activity.points > 0 && (
                        <span className="ml-2 text-boop-primary font-semibold">
                          +{activity.points} pts
                        </span>
                      )}
                    </div>
                    <div className="text-gray-400 text-xs">
                      {new Date(activity.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
